import type { TrpcClient } from "@common/api-interface/trpc-client";
import type {
  CalendarEvent,
  CalendarEventId,
  EventPayloadIdMap,
  EventPayloadItem,
  EventUpdateDelta,
} from "@common/types/calendar-event";
import { CommonEvents } from "./common-events";
import EventEmitter from "eventemitter3";
import _ from "lodash";

export type CalendarService = ReturnType<typeof makeCalendarService>;

export type EventServiceEventTypes = {
  ON_EVENT_UPDATE_SUCCESS: (params: { eventId: CalendarEventId }) => void;
  ON_EVENT_UPDATE_FAILED: (params: {
    eventId: CalendarEventId;
    error: string;
  }) => void;
};

export const makeCalendarService = ({ trpc }: { trpc: TrpcClient }) => {
  let _updateStash: EventPayloadIdMap = {};

  function pushCreate(event: CalendarEvent) {
    _updateStash[event.id] = {
      type: "CREATE",
      data: event,
      date: performance.now(),
    };
  }

  function pushUpdate(delta: EventUpdateDelta) {
    const current = _updateStash[delta.id];
    _updateStash[delta.id] = mergeEventUpdates(current, {
      type: "UPDATE",
      data: delta,
      date: performance.now(),
    });
  }

  function pushDelete(eventId: CalendarEventId) {
    _updateStash[eventId] = {
      type: "DELETE",
      id: eventId,
      date: performance.now(),
    };
  }

  const eventService = {
    getEvents,
    getEventsInRange,
    getCalendarConnections,
    disconnectCalendar,
    updateCalendarConnectionShowEvents,
    syncCalendars,
    createEvent,
    updateEvent,
    deleteEvent,
    dispatchUpdates,
    eventEmitter: new EventEmitter<EventServiceEventTypes>(),
    debouncedDispatch: _.debounce(dispatchUpdates, 1000),
  };

  return eventService;

  async function getEvents(): Promise<CalendarEvent[]> {
    return trpc.calendar.getEventsForUser.query();
  }

  async function getEventsInRange(
    from: number,
    to: number,
  ): Promise<CalendarEvent[]> {
    return trpc.calendar.getEventsForUser.query({ from, to });
  }

  async function getCalendarConnections() {
    return trpc.calendar.getCalendarConnections.query();
  }

  async function disconnectCalendar(connectionId: string) {
    return trpc.calendar.disconnectCalendar.mutate({ connectionId });
  }

  async function updateCalendarConnectionShowEvents(
    connectionId: string,
    showEvents: boolean,
  ) {
    return trpc.calendar.updateCalendarConnectionShowEvents.mutate({
      connectionId,
      showEvents,
    });
  }

  async function syncCalendars(): Promise<{
    processed: number;
    failed: number;
  }> {
    return trpc.calendar.syncCalendars.mutate();
  }

  function createEvent(
    event: CalendarEvent,
    { immediate } = { immediate: false },
  ) {
    pushCreate(event);
    if (immediate) {
      return eventService.dispatchUpdates();
    }
    eventService.debouncedDispatch();
  }

  function updateEvent(
    delta: EventUpdateDelta,
    { immediate } = { immediate: false },
  ) {
    pushUpdate(delta);
    if (immediate) {
      return eventService.dispatchUpdates();
    }
    eventService.debouncedDispatch();
  }

  function deleteEvent(
    eventId: CalendarEventId,
    { immediate } = { immediate: false },
  ) {
    pushDelete(eventId);
    if (immediate) {
      return eventService.dispatchUpdates();
    }
    eventService.debouncedDispatch();
  }

  async function dispatchUpdates() {
    const batch = _.cloneDeep(_updateStash);
    _updateStash = {};

    if (Object.keys(batch).length === 0) return;

    try {
      const res = await trpc.calendar.ingestEventUpdates.mutate(batch);

      for (const eventId of Object.keys(batch)) {
        eventService.eventEmitter.emit("ON_EVENT_UPDATE_SUCCESS", { eventId });
      }

      for (const fail of res.failed) {
        eventService.eventEmitter.emit("ON_EVENT_UPDATE_FAILED", {
          eventId: fail.id,
          error: fail.error,
        });
        // Re-merge the failed item back into the stash
        const original = batch[fail.id];
        if (original) {
          _updateStash[fail.id] = mergeEventUpdates(
            original,
            _updateStash[fail.id],
          );
        }
        CommonEvents.emit("PUBLISH_TOAST", {
          title: "Failed to save event",
          content: fail.error ?? "Unknown error",
          titleColor: "negative",
        });
      }
    } catch (error) {
      // On network error, re-merge everything back
      for (const eventId in batch) {
        _updateStash[eventId] = mergeEventUpdates(
          batch[eventId],
          _updateStash[eventId],
        );
      }
      CommonEvents.emit("PUBLISH_TOAST", {
        title: "Failed to save events",
        content: (error as Error).message || "Please try again later",
        titleColor: "negative",
      });
    }
  }
};

export function mergeEventUpdates(
  ...args: (EventPayloadItem | undefined)[]
): EventPayloadItem {
  const updates = args.filter(
    (item): item is EventPayloadItem => item !== undefined,
  );
  updates.sort((a, b) => a.date - b.date);

  const earliest = updates[0];
  if (!earliest) throw new Error("No updates provided");

  if (earliest.type === "DELETE") return earliest;

  if (earliest.type === "CREATE") {
    let data = earliest.data;
    let date = earliest.date;
    for (const item of updates) {
      if (item.type === "DELETE") return item;
      data = { ...data, ...item.data };
      date = item.date;
    }
    return { type: "CREATE", data: data as CalendarEvent, date };
  }

  // UPDATE
  let data = earliest.data;
  let date = earliest.date;
  let type: EventPayloadItem["type"] = "UPDATE";
  for (const item of updates) {
    if (item.type === "DELETE") return item;
    if (item.type === "CREATE") type = "CREATE";
    data = { ...data, ...item.data };
    date = item.date;
  }
  if (type === "CREATE")
    return { type: "CREATE", data: data as CalendarEvent, date };
  return { type: "UPDATE", data, date };
}
